/**
 * Describes an outgoing request body (or query params) for a Rollbar report without including any
 * of its values.
 *
 * By the time the axios error interceptor sees `error.config.data`, axios has already serialized a
 * JSON body to a string. Rollbar's `scrubFields` only redacts values under matching object keys, so
 * it cannot see inside that string: emails, plaintext passwords and reset tokens were stored
 * verbatim (VH-3151). Rather than try to redact values, report only the body's shape: its type and
 * top-level key names, which is what debugging a failed request actually needs.
 *
 * Kept dependency-free so it can be unit tested without loading the axios plugin's module graph.
 */

export interface RequestBodySummary {
  type: string
  keys?: string[]
  length?: number
}

const MAX_KEYS = 50

const UUID_LIKE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
const SAFE_KEY = /^[A-Za-z_][\w.\-[\]]{0,63}$/

/**
 * Key names are normally schema names (`email`, `token`), but a body keyed by data (a map from
 * patient uuid to something, or a free-text key) would turn the key itself into the leak. Keep only
 * keys that look like identifiers, and mask anything that looks like an id or carries a long run of
 * digits.
 */
function safeKey(key: string): string {
  if (!SAFE_KEY.test(key) || UUID_LIKE.test(key) || /^pat_/i.test(key) || /\d{4,}/.test(key))
    return '[redacted-key]'

  return key
}

function summarizeKeys(keys: Iterable<string>): string[] {
  return [...new Set(Array.from(keys, safeKey))].slice(0, MAX_KEYS)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object')
    return false

  const proto = Object.getPrototypeOf(value)

  return proto === Object.prototype || proto === null
}

export function summarizeRequestBody(data: unknown): RequestBodySummary | undefined {
  if (data === undefined || data === null || data === '')
    return undefined

  if (typeof FormData !== 'undefined' && data instanceof FormData)
    return { type: 'form-data', keys: summarizeKeys(data.keys()) }

  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams)
    return { type: 'url-encoded', keys: summarizeKeys(data.keys()) }

  if (
    (typeof Blob !== 'undefined' && data instanceof Blob)
    || (typeof ArrayBuffer !== 'undefined' && (data instanceof ArrayBuffer || ArrayBuffer.isView(data)))
  )
    return { type: 'binary' }

  if (typeof data === 'string') {
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    } catch {
      // Not JSON (e.g. a url-encoded form body). Its values can't be told apart from its keys
      // safely, so say only that it was text.
      return { type: 'text' }
    }

    return summarizeParsed(parsed, 'json')
  }

  return summarizeParsed(data, 'object')
}

function summarizeParsed(value: unknown, objectType: string): RequestBodySummary {
  if (Array.isArray(value))
    return { type: 'array', length: value.length }

  if (isPlainObject(value))
    return { type: objectType, keys: summarizeKeys(Object.keys(value)) }

  // A bare JSON primitive, or a class instance we don't know how to describe.
  return { type: value === null ? 'null' : typeof value }
}
